import { computed, nextTick, ref, Ref, watch, WatchStopHandle } from 'vue';
import { BudgetTypes } from '@/models/budget.interface';
import { ValidationProperties } from '@/models/validation.interface';
import { CampaignType } from '@studyportals/campaign-management-api-interface';
import { ContextualTooltip } from '@studyportals/tooltip';
import { LoadingStatuses } from '@/models/loading.interface';
import validationPresenter from '@/presenters/validation-presenter';
import store from '@/store';

export default class BudgetWrapperCode {
	public listingBudgetTooltipWrapper: Ref<HTMLDivElement | null> = ref(null);
	public customProductsBudgetTooltipWrapper: Ref<HTMLDivElement | null> = ref(null);
	private unwatch: WatchStopHandle | null = null;

	public get budgetTypeListing(): BudgetTypes {
		return BudgetTypes.LISTING;
	}

	public get budgetTypeCustom(): BudgetTypes {
		return BudgetTypes.CUSTOM;
	}

	public isListingBudgetFieldDisabled = computed((): boolean => {
		const campaignType = store.getters.campaignType();
		return campaignType === CampaignType.CUSTOM_PRODUCTS;
	});

	public isLoading = computed((): boolean => {
		const loadingStatus = store.getters.loadingStatusPrimaryData();
		return loadingStatus === LoadingStatuses.ACTIVE;
	});

	public listingBudgetDisabledMessage = computed((): string => {
		if (!this.isListingBudgetFieldDisabled.value) {
			return '';
		}

		return 'This field is disabled for custom-products-only campaigns.';
	});

	public validationMessage = computed((): string => {
		if (this.isListingFieldValidatedAndInvalid.value) {
			return 'This field must be a valid number';
		}

		return 'This field must be completed before proceeding';
	});

	public isListingFieldValidatedAndMissing = computed((): boolean => {
		return validationPresenter.isPropertyMissing(ValidationProperties.CURRENCY)
			|| (validationPresenter.isPropertyMissing(ValidationProperties.LISTING_BUDGET) && !this.isCampaignTypeCustomProductsOnly.value);
	});

	public isListingFieldValidatedAndInvalid = computed((): boolean => {
		return validationPresenter.isPropertyInvalid(ValidationProperties.LISTING_BUDGET) && !this.isCampaignTypeCustomProductsOnly.value;
	});

	public isCustomFieldValidatedAndMissing = computed((): boolean => {
		return validationPresenter.isPropertyMissing(ValidationProperties.CURRENCY)
			|| validationPresenter.isPropertyMissing(ValidationProperties.CUSTOM_PRODUCTS_BUDGET);
	});

	public isCustomFieldValidatedAndInvalid = computed((): boolean => {
		return validationPresenter.isPropertyInvalid(ValidationProperties.CUSTOM_PRODUCTS_BUDGET);
	});

	private isCampaignTypeCustomProductsOnly = computed((): boolean => {
		return this.campaignType.value === CampaignType.CUSTOM_PRODUCTS;
	});

	private campaignType = computed((): CampaignType | null => {
		return store.getters.campaignType();
	});

	public mounted(): void {
		this.unwatch = watch(this.isLoading, this.prepareTooltips.bind(this));
		void this.prepareTooltips();
	}

	public unmounted(): void {
		if (!this.unwatch) {
			return;
		}

		this.unwatch();
	}

	public async prepareTooltips(): Promise<void> {
		if (this.isLoading.value) {
			return;
		}

		await nextTick();

		if (!this.listingBudgetTooltipWrapper.value || !this.customProductsBudgetTooltipWrapper.value) {
			return;
		}

		new ContextualTooltip({
			element: this.listingBudgetTooltipWrapper.value,
			content: 'This is the budget allocated for the listing of your programmes.',
			position: 'top'
		});

		new ContextualTooltip({
			element: this.customProductsBudgetTooltipWrapper.value,
			content: 'This is the budget allocated for your custom products such as emails, leads, etc.',
			position: 'top'
		});
	}
}
